import { stockSearchStateSchema, stockStateToFilters } from '@repo/shared-types';

import type { StockSearchState } from '@repo/shared-types';

export const PAGE_SIZE = 24;

type StateKey = keyof StockSearchState;

const STATE_KEYS: StateKey[] = [
  'keywords',
  'make',
  'model',
  'body_type',
  'fuel_type',
  'transmission',
  'colour',
  'price_min',
  'price_max',
  'year_min',
  'year_max',
  'mileage_max',
  'postcode',
  'radius_miles',
  'sort',
];

/** Parses each URL value on its own, so one bad value never wipes the others. */
export const readSearchUrl = (params: URLSearchParams) => {
  const state: Record<string, unknown> = {};
  for (const key of STATE_KEYS) {
    const raw = params.get(key);
    if (raw === null) continue;
    const parsed = stockSearchStateSchema.safeParse({ [key]: raw });
    const value = parsed.success ? parsed.data[key] : undefined;
    if (value !== undefined) state[key] = value;
  }
  return {
    state: state as StockSearchState,
    page: Math.max(Number(params.get('page')) || 1, 1),
    source: params.get('source') || 'all',
  };
};

export const writeSearchUrl = (
  state: StockSearchState,
  page: number,
  source: string,
  keep?: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams(keep);
  for (const key of [...STATE_KEYS, 'page', 'source']) next.delete(key);
  for (const key of STATE_KEYS) {
    const value = state[key];
    if (value !== undefined && value !== '') next.set(key, String(value));
  }
  if (page > 1) next.set('page', String(page));
  if (source !== 'all') next.set('source', source);
  return next;
};

export const toSearchRequest = (state: StockSearchState, page: number, sources: string[]) => {
  const filters = stockStateToFilters(state);
  return {
    ...(state.keywords && { query: state.keywords }),
    ...(Object.keys(filters).length > 0 && { filters }),
    ...(state.sort && { sort: state.sort }),
    ...(state.postcode && { postcode: state.postcode }),
    ...(state.radius_miles && { radius_miles: state.radius_miles }),
    page,
    limit: PAGE_SIZE,
    sources,
  };
};

const money = (n: number) => `£${n.toLocaleString('en-GB')}`;
const CHIP_LABELS: Partial<Record<StateKey, (v: string | number) => string>> = {
  keywords: (v) => `"${v}"`,
  make: (v) => `Make: ${v}`,
  model: (v) => `Model: ${v}`,
  body_type: (v) => `Body: ${v}`,
  fuel_type: (v) => `Fuel: ${v}`,
  transmission: (v) => `Gearbox: ${v}`,
  colour: (v) => `Colour: ${v}`,
  price_min: (v) => `From ${money(Number(v))}`,
  price_max: (v) => `Up to ${money(Number(v))}`,
  year_min: (v) => `From ${v}`,
  year_max: (v) => `Up to ${v}`,
  mileage_max: (v) => `Under ${Number(v).toLocaleString('en-GB')} mi`,
  postcode: (v) => `Near ${String(v).toUpperCase()}`,
  radius_miles: (v) => `Within ${v} mi`,
};

/** Removable chips for every active filter (sort is shown in its own control). */
export const filterChips = (state: StockSearchState) =>
  STATE_KEYS.filter((key) => key !== 'sort' && state[key] !== undefined && CHIP_LABELS[key]).map(
    (key) => ({ key, label: CHIP_LABELS[key]!(state[key] as string | number) }),
  );

/** One-click ways out of an empty result, most useful first (max 3). */
export const emptySuggestions = (state: StockSearchState) => {
  const out: { label: string; patch: Partial<Record<StateKey, undefined | number>> }[] = [];
  if (state.price_max !== undefined || state.price_min !== undefined)
    out.push({
      label: 'Remove price limit',
      patch: { price_min: undefined, price_max: undefined },
    });
  if (state.transmission) out.push({ label: 'Any gearbox', patch: { transmission: undefined } });
  if (state.fuel_type) out.push({ label: 'Any fuel', patch: { fuel_type: undefined } });
  if (state.postcode && (state.radius_miles ?? 0) < 50)
    out.push({ label: 'Widen to 50 mi', patch: { radius_miles: 50 } });
  if (state.mileage_max !== undefined)
    out.push({ label: 'Any mileage', patch: { mileage_max: undefined } });
  return out.slice(0, 3);
};

/** 'all' → DealerKit + every connector; anything else → just that source. */
export const toSources = (source: string, connectorIds: string[]): string[] =>
  source === 'all' ? ['dealerkit', ...connectorIds] : [source];
